import React from 'react';
import PropTypes from 'prop-types';
import { isNil } from 'lodash-es';
import { getPriceStringV2 } from 'client/site-modules/shared/utils/price-utils';
import { LeaseDealsCta } from 'site-modules/lease-deals/components/lease-deals-cta/lease-deals-cta';
import { VdpPopoverItem } from 'client/site-modules/shared/components/inventory/vdp-popover-item/vdp-popover-item';
import { EngagementPanel } from 'site-modules/lease-deals/components/engagement-panel/engagement-panel';

import './lease-deals-card-description.scss';

export const LeaseDealsCardDescriptionUI = ({
  year,
  make,
  term,
  msrp,
  model,
  payment,
  dueAtSigning,
  bodyType = undefined,
  styleId,
  id,
  effectivePayment,
  effectivePaymentMedian = undefined,
  paymentMedian = undefined,
  isShop4931ZeroDownRoute = false,
  showCta = false,
  errorImage = '',
  ...restProps
}) => {
  const formattedMsrp = getPriceStringV2(msrp);
  const displayedPayment = isShop4931ZeroDownRoute ? Math.round(effectivePayment) : payment;
  const displayedDueAtSigning = isShop4931ZeroDownRoute ? effectivePayment : dueAtSigning;
  const formattedDueAtSigning = getPriceStringV2(displayedDueAtSigning, 'Contact dealer', { allowZero: true });
  const belowAveragePayment = !isNil(paymentMedian) ? paymentMedian - payment : undefined;
  const zeroDownBelowAveragePayment =
    effectivePaymentMedian > effectivePayment ? effectivePaymentMedian - effectivePayment : undefined;
  const displayedBelowAverage = isShop4931ZeroDownRoute ? zeroDownBelowAveragePayment : belowAveragePayment;
  const formattedBelowAverage = getPriceStringV2(displayedBelowAverage);

  return (
    <div className="lease-deals-card-description h-100 d-flex flex-column">
      <div className="vehicle-info">
        <div className="d-flex justify-content-between gap-1">
          <h2 className="mb-0 size-20 fw-medium">{`${year} ${make} ${model}`}</h2>
          <div className="price-info d-flex justify-content-end align-items-center gap-0_25">
            <p className="price fw-bold mb-0" aria-label={`${displayedPayment} per month`}>
              ${displayedPayment}
            </p>
            <span className="size-14" aria-hidden>
              /mo*
            </span>
          </div>
        </div>
        <div className="payment-info d-flex gap-1 justify-content-between align-items-center">
          <span className="lease-deals-msrp size-14">MSRP: {formattedMsrp}</span>
          {displayedBelowAverage > 0 && (
            <span className="m-0 text-cool-gray-30 fw-normal size-14 white-space-nowrap">
              {formattedBelowAverage}/mo. below avg.
              <VdpPopoverItem
                tooltip={`Comparison is based off the median lease payment estimate for similar ${bodyType || 'vehicle'}s with a 36 month term and 12,000 annual mile lease.`}
                iconClassName="px-0_25 pt-0_5 pb-0 p-md-0 pos-r"
                innerClassName="pos-r lease-deals-tooltip-inner"
                screenReaderTooltipLabel={`${formattedBelowAverage} per month below avg.`}
                offset={[0, 12]}
              />
            </span>
          )}
        </div>
      </div>
      <div className="vehicle-details d-flex flex-column justify-content-end flex-grow-1">
        <div className="terms-info d-flex flex-wrap flex-sm-nowrap justify-content-between align-items-center my-1_5 px-1_5 py-1 rounded-16 text-center">
          <div className="flex-fill mb-0">
            <div className="text-cool-gray-40 size-14 fw-medium">Term</div>
            <div className="text-cool-gray-20 fw-bold mt-0_25">{term} months</div>
          </div>

          <div className="border-start mx-1" />

          <div className="flex-fill mb-0">
            <div className="text-cool-gray-40 size-14 fw-medium">Due at signing</div>
            <div className="text-cool-gray-20 fw-bold mt-0_25">{formattedDueAtSigning}</div>
          </div>
        </div>
        <LeaseDealsCta
          {...restProps}
          year={year}
          make={make}
          term={term}
          msrp={msrp}
          model={model}
          payment={payment}
          dueAtSigning={dueAtSigning}
          bodyType={bodyType}
          styleId={styleId}
          id={id}
          effectivePayment={effectivePayment}
          effectivePaymentMedian={effectivePaymentMedian}
          paymentMedian={paymentMedian}
          showCta={showCta}
          errorImage={errorImage}
          buttonProps={{
            className: 'lease-deals-button btn-blue-50 pos-r text-transform-none fw-medium w-100 py-0_5 rounded-pill',
          }}
          formName="oem_lease_deal_multi"
        />
        <EngagementPanel styleId={styleId} className="mt-1" make={make} model={model} year={year} id={id} />
      </div>
    </div>
  );
};

LeaseDealsCardDescriptionUI.propTypes = {
  id: PropTypes.string.isRequired,
  msrp: PropTypes.number.isRequired,
  showCta: PropTypes.bool,
  payment: PropTypes.number.isRequired,
  photo: PropTypes.shape({}),
  errorImage: PropTypes.string,
  dueAtSigning: PropTypes.number.isRequired,
  year: PropTypes.number.isRequired,
  make: PropTypes.string.isRequired,
  term: PropTypes.number.isRequired,
  model: PropTypes.string.isRequired,
  styleId: PropTypes.number.isRequired,
  bodyType: PropTypes.string,
  effectivePayment: PropTypes.number.isRequired,
  effectivePaymentMedian: PropTypes.number,
  paymentMedian: PropTypes.number,
  isShop4931ZeroDownRoute: PropTypes.bool,
};

export const LeaseDealsCardDescription = LeaseDealsCardDescriptionUI;
